import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, api, apiBlob } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { formatDate } from "../lib/hooks.ts";
import { Empty, ErrorAlert, Modal, Spinner } from "./ui.tsx";

interface BiosReference {
  uid: string;
  make: string;
  model: string;
  version: string;
  filename: string;
  sha256: string;
  sizeBytes: number;
  sourceUrl: string;
  notes: string;
  createdBy: string;
  createdAt: string;
}

const MAX_BYTES = 64 * 1024 * 1024;

function humanSize(bytes: number): string {
  if (!bytes) return "—";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function BiosReferences({ make, model }: { make: string; model: string }) {
  const { can } = useAuth();
  const [items, setItems] = useState<BiosReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ make: make || "", model: model || "", version: "", url: "", notes: "" });
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      if (make) q.set("make", make);
      if (model) q.set("model", model);
      const res = await api<{ references: BiosReference[] }>(`/api/v3/bios-references?${q.toString()}`);
      setItems(res.references ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao carregar imagens de BIOS");
    } finally {
      setLoading(false);
    }
  }, [make, model]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setForm((f) => ({ ...f, make: make || f.make, model: model || f.model }));
  }, [make, model]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (!form.model.trim()) {
      setError("Informe o modelo da placa.");
      return;
    }
    if (!form.version.trim()) {
      setError("Informe a versao da BIOS.");
      return;
    }
    if (!file && !form.url.trim()) {
      setError("Selecione o arquivo da BIOS ou informe a URL oficial do fabricante.");
      return;
    }
    if (file && file.size > MAX_BYTES) {
      setError("Arquivo maior que 64 MB.");
      return;
    }
    setSaving(true);
    try {
      let dataBase64: string | undefined;
      if (file) {
        dataBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("Falha ao ler o arquivo selecionado"));
          reader.readAsDataURL(file);
        });
      }
      await api("/api/v3/bios-references", {
        method: "POST",
        body: {
          make: form.make.trim(),
          model: form.model.trim(),
          version: form.version.trim(),
          notes: form.notes.trim(),
          url: file ? "" : form.url.trim(),
          filename: file?.name || "",
          dataBase64,
        },
      });
      setMessage("Imagem de BIOS adicionada ao catalogo.");
      setOpen(false);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setForm((f) => ({ ...f, version: "", url: "", notes: "" }));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao salvar a imagem");
    } finally {
      setSaving(false);
    }
  }

  async function download(item: BiosReference) {
    try {
      const blob = await apiBlob(`/api/v3/bios-references/${item.uid}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao baixar a imagem");
    }
  }

  async function remove(item: BiosReference) {
    if (!window.confirm(`Excluir a imagem ${item.version} (${item.filename}) do catalogo?`)) return;
    try {
      await api(`/api/v3/bios-references/${item.uid}`, { method: "DELETE" });
      setMessage("Imagem removida do catalogo.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao excluir a imagem");
    }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <strong>Imagem de referencia do fabricante</strong>
        <span className="subtle" style={{ fontSize: 12 }}>
          Imagem oficial da BIOS para recuperacao/reflash. Nao e um dump do firmware atual.
        </span>
        <span className="spacer" />
        {can("settings.write") ? (
          <button className="btn sm primary" type="button" onClick={() => setOpen(true)}>
            Adicionar imagem
          </button>
        ) : null}
      </div>

      {message ? <div className="alert info">{message}</div> : null}
      {error ? <ErrorAlert message={error} /> : null}

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty>
          {model
            ? `Nenhuma imagem oficial cadastrada para o modelo ${model}.`
            : "Modelo da placa nao identificado; cadastre a imagem informando o modelo."}
        </Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Versao</th>
                <th>Arquivo</th>
                <th>Tamanho</th>
                <th>Modelo</th>
                <th>Adicionada</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.uid}>
                  <td className="mono">{item.version}</td>
                  <td>
                    <div className="cell-title">{item.filename}</div>
                    <div className="cell-sub mono" title={item.sha256}>
                      SHA-256: {item.sha256.slice(0, 16)}…
                    </div>
                  </td>
                  <td>{humanSize(item.sizeBytes)}</td>
                  <td className="cell-sub">{[item.make, item.model].filter(Boolean).join(" / ")}</td>
                  <td className="cell-sub">{formatDate(item.createdAt)}</td>
                  <td>
                    <div className="toolbar" style={{ gap: 6, flexWrap: "nowrap" }}>
                      <button className="btn sm" type="button" onClick={() => download(item)}>
                        Baixar
                      </button>
                      {can("settings.write") ? (
                        <button className="btn sm danger" type="button" onClick={() => remove(item)}>
                          Excluir
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <Modal
          title="Adicionar imagem de BIOS"
          onClose={() => {
            if (!saving) setOpen(false);
          }}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setOpen(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn primary" type="submit" form="bios-ref-form" disabled={saving}>
                {saving ? "Salvando..." : "Salvar imagem"}
              </button>
            </>
          }
        >
          <form id="bios-ref-form" onSubmit={submit}>
            <div className="grid cols-2">
              <div className="field">
                <label htmlFor="bref-make">Fabricante</label>
                <input
                  id="bref-make"
                  className="input"
                  value={form.make}
                  onChange={(e) => setForm({ ...form, make: e.target.value })}
                  placeholder="ex: Dell, Lenovo"
                />
              </div>
              <div className="field">
                <label htmlFor="bref-model">Modelo da placa</label>
                <input
                  id="bref-model"
                  className="input"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  placeholder="ex: N8450"
                  required
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="bref-version">Versao da BIOS</label>
              <input
                id="bref-version"
                className="input"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="ex: 1.12.0"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="bref-file">Arquivo oficial (ate 64 MB)</label>
              <input
                id="bref-file"
                ref={fileRef}
                className="input"
                type="file"
                accept=".zip,.exe,.bin,.rom,.cap,.fd,.img,.rar,.7z"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="field">
              <label htmlFor="bref-url">ou URL oficial do fabricante</label>
              <input
                id="bref-url"
                className="input"
                value={form.url}
                disabled={!!file}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://..."
              />
              <span className="subtle" style={{ fontSize: 12 }}>
                O servidor baixa a URL. Sites que exigem navegador/JS podem falhar — nesse caso envie o arquivo.
              </span>
            </div>
            <div className="field">
              <label htmlFor="bref-notes">Observacoes</label>
              <input
                id="bref-notes"
                className="input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="ex: BIOS oficial de recuperacao para reflash via USB"
              />
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
